/**
 * @fileoverview Input/output schemas for short-story-modifier-v1.
 *
 * This agent takes a natural language change request from the author and
 * applies modifications across three planning objects for short-form fiction:
 * plot threads, character arcs, and the writtenPlot.
 *
 * Key difference from story-modifier-v1: No storylines or beats. Uses plot
 * threads (title, summary, threadQuestion) instead of storylines with beats.
 * Character arcs are compact summaries without beat tracking. The writtenPlot
 * is the single source of truth.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 *
 * This is a Tier 2 agent: the result is shown to the user for confirmation
 * before being persisted.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { CharacterAttributesSchema } from '@/domains/characters/schemas/character-sub-entities-v2';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.writtenPlot.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characterArcs.schema)
  .merge(AGENT_SCHEMA_OBJECTS.characters.schema)
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.projectedWordCount.schema)
  .extend({
    plotThreads: z.array(z.object({
      id: z.string().describe('Plot thread ID.'),
      title: z.string().describe('Short, evocative title for the thread.'),
      summary: z.string().describe('Context making the question meaningful.'),
      threadQuestion: z.string().describe('The dramatic question driving this thread.'),
    })).optional().describe('Existing plot threads for the story.'),
    changeRequest: z.string().describe(
      'The author\'s natural language description of the changes they want applied to the story. ' +
      'This can reference specific phases, characters, plot threads, or narrative elements.'
    ),
    scopeGuidance: z.string().optional().describe(
      'Rendered scope guidance string that specifies the target paragraph density for the writtenPlot ' +
      'based on the story\'s projected word count.'
    ),
  });

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Flat structure for Gemini compatibility)
// =============================================================================

/**
 * Agent output for a modified plot thread.
 */
const ModifiedPlotThreadSchema = z.object({
  id: z.string().describe('Plot thread ID. Preserve existing IDs for modified threads; use new UUIDs for new threads.'),
  title: z.string().describe(
    'Short, evocative title for the narrative thread (3-5 words).'
  ),
  summary: z.string().describe(
    'Context making the question meaningful — what this thread is about and why it matters. ' +
    '2-3 sentences capturing the thread\'s trajectory from introduction to resolution.'
  ),
  threadQuestion: z.string().describe(
    'The dramatic question driving this thread (e.g., "Will she forgive him?"). ' +
    'Must be a genuine question that creates tension and needs resolution within the story.'
  ),
});

/**
 * Agent output for a modified character arc — short-form only (no beats).
 */
const ModifiedCharacterArcSchema = z.object({
  characterId: z.string().describe('The ID of the character whose arc was modified. Must match an existing character.'),
  characterName: z.string().describe('The name of the character (for readability).'),
  arcName: z.string().describe(
    'A short, evocative title for the arc (e.g., "From Coward to Hero", "The Price of Ambition").'
  ),
  arcSummary: z.string().describe(
    'A detailed 3-5 sentence summary of the character\'s internal transformation. ' +
    'Captures starting psychological state, key turning points, and resolution. ' +
    'Must be specific about what drives each shift.'
  ),
});

/**
 * Character change tracking — same structure as story-modifier-v1.
 */
const CharacterChangeSchema = z.object({
  characterId: z.string().describe('The character ID. Use the existing ID for modified/removed characters; generate a new UUID for added characters.'),
  action: z.enum(['added', 'removed', 'modified']).describe('The type of change: "added" for new characters, "removed" for deleted characters, "modified" for property changes.'),
  characterName: z.string().describe('The character\'s name.'),
  category: z.enum(['major', 'minor', 'extra']).describe('The character\'s importance level.'),
  modifiedProperties: CharacterAttributesSchema.partial().optional().describe(
    'An object containing only the character properties that were changed. ' +
    'Omit unchanged properties. Required for "added" and "modified" actions; omit for "removed".'
  ),
});

export const OutputSchema = z.object({
  summary: z.string().describe(
    'A concise overview of the plot (50-100 words) that weaves together the story summary, its central theme, and the main conflict. ' +
    'This summary is displayed as a subtitle on the Active Story Outline card and used as context by other AI agents. ' +
    'Must reflect the modified plot content.'
  ),
  commentary: z.string().optional().describe(
    'A summary explaining what changes were made across all planning objects and why. ' +
    'Be specific about which plot threads, characters, character arcs, and plot sections were modified. ' +
    'Note any plot threads or characters that were added or removed. ' +
    'Explain how the changes maintain narrative coherence across the objects.'
  ),
  characters: z.array(CharacterChangeSchema).default([]).describe(
    'An array of character change operations. Only include characters that were added, removed, or had their properties modified. ' +
    'Return an empty array if no character changes are needed.'
  ),
  plotThreads: z.array(ModifiedPlotThreadSchema).describe(
    'The complete, updated array of plot threads. May add new threads or remove existing ones. ' +
    'Preserve IDs for threads that were not changed. ' +
    'Every thread from the input must appear in the output unless explicitly removed by the change request. ' +
    'For flash fiction: 1-2 threads. For standard short fiction: 2-4 threads.'
  ),
  characterArcs: z.array(ModifiedCharacterArcSchema).describe(
    'The updated character arcs for all characters remaining in the story (including newly added ones). ' +
    'Omit arcs for removed characters. Include arcs for added characters if they are major or minor. ' +
    'Arcs are compact summaries — no beat tracking for short-form.'
  ),
  writtenPlot: z.string().describe(
    'The updated plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Write in present tense, third person. ' +
    'The narrative should reflect the changes made to plot threads and character arcs. ' +
    'This is the single source of truth for the story\'s structural arc.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
